import { NavLink } from "react-router-dom";
import type { Project, Team } from "../types";

interface Props {
  /** Structural rather than firebase's `User`: it is two strings and a sign-out. */
  user: { photoURL?: string | null; email?: string | null };
  teams: Team[];
  projects: Project[];
  project: Project | null;
  selectProject: (projectId: string) => void;
  /** Accounts switched on *and* healthy -- the badge on Providers. */
  liveCount: number;
  toolCount: number;
  /** Invitations waiting on this user's answer -- the badge on Teams. */
  inviteCount: number;
  onSignOut: () => void;
}

/**
 * The shell's chrome: who you are, which project you are in, and where you can go.
 *
 * Its own component because it has two layouts, not one that shrinks. On a wide screen the
 * four parts share a row. On a phone the nav leaves the bar entirely for a tab bar fixed to
 * the bottom of the screen -- in reach of a thumb, out of the way of the switcher, and
 * still there while the page scrolls. Both are this markup; the difference is in
 * `styles.css`, which is why the icons below are always rendered and shown only there.
 */
export default function TopBar({
  user,
  teams,
  projects,
  project,
  selectProject,
  liveCount,
  toolCount,
  inviteCount,
  onSignOut,
}: Props) {
  return (
    <header className="topbar">
      <div className="brand">
        <span className="brand-mark">🔌</span>
        <span className="brand-name">MochiConnector</span>
      </div>

      {/* Which project everything on screen belongs to. A select, not a page: the
          switch has to be reachable from anywhere, because every page is scoped by it. */}
      {projects.length > 0 && (
        <label className="project-switch">
          <span className="project-switch-label">Project</span>
          <select
            value={project?.id ?? ""}
            onChange={(e) => selectProject(e.target.value)}
            aria-label="Current project"
          >
            {teams.map((team) => (
              // The personal team is presented as the home of private projects, not as
              // a team -- its stored name is an implementation detail.
              <optgroup
                key={team.id}
                label={team.personal ? "Private projects" : team.name}
              >
                {projects
                  .filter((p) => p.teamId === team.id)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>
        </label>
      )}

      <nav className="nav" aria-label="Sections">
        <NavLink to="/setup" className={navClass}>
          <NavIcon name="setup" />
          Setup
        </NavLink>
        <NavLink to="/providers" className={navClass}>
          <NavIcon name="providers" />
          Providers
          {/* The number that answers "is anything actually switched on?" from either
              page -- otherwise you have to go to Providers to find out. */}
          {liveCount > 0 && <span className="nav-count">{liveCount}</span>}
        </NavLink>
        {/* Tools cuts across accounts, which is why it is a page of its own rather than
            a control on Providers: the question it answers ("what can do X?") has no
            account in it, and the grid is organised entirely by account. */}
        <NavLink to="/tools" className={navClass}>
          <NavIcon name="tools" />
          Tools
          {toolCount > 0 && <span className="nav-count">{toolCount}</span>}
        </NavLink>
        {/* What the tools *made*, as opposed to what they can do. Its own entry rather
            than a panel on a card: a generated file belongs to the account that made it,
            but nobody looks for one that way -- they want every file, newest first. */}
        <NavLink to="/files" className={navClass}>
          <NavIcon name="files" />
          Files
        </NavLink>
        <NavLink to="/teams" className={navClass}>
          <NavIcon name="teams" />
          Teams
          {/* Pending invitations for *you* -- the one thing on that page that is
              waiting on your answer, so it is worth a badge from any page. */}
          {inviteCount > 0 && <span className="nav-count">{inviteCount}</span>}
        </NavLink>
      </nav>

      <div className="account">
        {user.photoURL && <img src={user.photoURL} alt="" className="avatar" />}
        <span className="account-email">{user.email}</span>
        <button className="btn btn-ghost btn-sm" onClick={onSignOut}>
          Sign out
        </button>
      </div>
    </header>
  );
}

function navClass({ isActive }: { isActive: boolean }) {
  return `nav-link ${isActive ? "nav-link-active" : ""}`;
}

/**
 * The tab-bar glyphs. Decoration beside a label on a wide screen -- and hidden there --
 * but load-bearing in the bottom bar, where five labels only fit on one line of a 360px
 * screen at a size worth reading if the icon is carrying the recognition.
 *
 * `currentColor` throughout, so the active tab's accent applies to the glyph too, and
 * `aria-hidden` because the label right underneath already says it.
 */
function NavIcon({
  name,
}: {
  name: "setup" | "providers" | "tools" | "files" | "teams";
}) {
  return (
    <svg
      className="nav-icon"
      width="18"
      height="18"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {name === "setup" && (
        // Sliders: the one page that is settings rather than contents.
        <>
          <path d="M2 4.5h5M11 4.5h3M2 11.5h3M9 11.5h5" />
          <circle cx="9" cy="4.5" r="1.6" />
          <circle cx="7" cy="11.5" r="1.6" />
        </>
      )}
      {name === "providers" && (
        // A plug, the same figure as the brand mark: connected accounts.
        <>
          <path d="M6 1.8v3M10 1.8v3" />
          <path d="M3.6 4.8h8.8v2.6a4.4 4.4 0 0 1-8.8 0V4.8Z" />
          <path d="M8 11.8v2.4" />
        </>
      )}
      {name === "tools" && (
        // A grid: many small things, listed.
        <>
          <rect x="2" y="2" width="5" height="5" rx="1.2" />
          <rect x="9" y="2" width="5" height="5" rx="1.2" />
          <rect x="2" y="9" width="5" height="5" rx="1.2" />
          <rect x="9" y="9" width="5" height="5" rx="1.2" />
        </>
      )}
      {name === "files" && (
        // A document with a folded corner: a thing that was produced and kept.
        <>
          <path d="M9 1.8H4.5A1.2 1.2 0 0 0 3.3 3v10a1.2 1.2 0 0 0 1.2 1.2h7a1.2 1.2 0 0 0 1.2-1.2V5.4Z" />
          <path d="M9 1.8v3.6h3.7" />
        </>
      )}
      {name === "teams" && (
        // Two people: the page is about who else is in this.
        <>
          <circle cx="6" cy="5.2" r="2.4" />
          <path d="M1.8 13.4a4.2 4.2 0 0 1 8.4 0" />
          <path d="M11 3.2a2.4 2.4 0 0 1 0 4.6" />
          <path d="M11.6 9.6a4.2 4.2 0 0 1 2.6 3.8" />
        </>
      )}
    </svg>
  );
}
